import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useT } from '../lib/i18n';

export default function History() {
  const { t, lang } = useT();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get('/sessions')
      .then(r => setSessions(r.data))
      .catch(e => console.error('History sessions load failed:', e))
      .finally(() => setLoading(false));
  }, []);

  const openSession = async (id) => {
    if (openId === id) {
      setOpenId(null);
      setDetail(null);
      return;
    }
    setOpenId(id);
    setDetail(null);
    try {
      const r = await api.get(`/sessions/${id}`);
      setDetail(r.data);
    } catch (e) {
      console.error('History session detail failed:', e);
    }
  };

  const formatDate = (s) => {
    try {
      const d = new Date(s);
      return d.toLocaleString(lang === 'fi' ? 'fi-FI' : undefined, { dateStyle: 'medium', timeStyle: 'short' });
    } catch { return s; }
  };

  const phaseLabel = (type) => t(`history.label.${type}`, type);

  return (
    <div className="min-h-screen flex flex-col fade-in" data-testid="history-screen">
      <header className="w-full px-4 sm:px-10 lg:px-16 pt-6 sm:pt-10 lg:pt-12 pb-6 sm:pb-8 grid grid-cols-3 items-center gap-2">
        <Link
          to="/"
          className="justify-self-start text-[0.7rem] sm:text-xs tracking-[0.18em] uppercase text-[#000000] hover:opacity-60 transition-opacity duration-300 font-pressura"
          style={{ fontWeight: 700 }}
          data-testid="history-back-link"
        >
          {t('nav.back')}
        </Link>
        <span aria-hidden="true" className="justify-self-center" />
        <span
          className="justify-self-end text-[0.7rem] sm:text-xs tracking-[0.18em] uppercase text-[#000000] opacity-60 font-pressura"
          style={{ fontWeight: 700 }}
          data-testid="history-page-label"
        >
          {t('history.title')}
        </span>
      </header>

      <main className="flex-1 py-16">
        <div className="ctl-container w-full">
          {loading && <p className="ctl-helper" data-testid="history-loading">{t('history.loading')}</p>}
          {!loading && sessions.length === 0 && (
            <p className="ctl-helper" data-testid="history-empty">{t('history.empty')}</p>
          )}
          <ul className="divide-y divide-[#E3E1DB]" data-testid="history-list">
            {sessions.map((s) => (
              <li key={s.id} className="py-6">
                <button
                  className="w-full text-left flex items-center justify-between"
                  onClick={() => openSession(s.id)}
                  data-testid={`history-item-${s.id}`}
                >
                  <span className="text-[#1C1B1A]">{formatDate(s.created_at)}</span>
                  <span className="ctl-helper text-xs">{s.completed ? t('history.completed') : t('history.incomplete')}</span>
                </button>
                {openId === s.id && (
                  <div className="mt-6 space-y-4 pl-4 border-l border-[#E3E1DB]" data-testid={`history-detail-${s.id}`}>
                    {!detail && <p className="ctl-helper text-sm">{t('history.entryLoading')}</p>}
                    {detail && detail.entries.length === 0 && (
                      <p className="ctl-helper text-sm">{t('history.noEntries')}</p>
                    )}
                    {detail && detail.entries.map(e => (
                      <div key={e.id}>
                        <div className="ctl-eyebrow text-xs mb-1">{phaseLabel(e.type)}</div>
                        <p className="text-[#1C1B1A]">{e.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>

          <div
            className="mt-32 sm:mt-40 mb-4 flex justify-center select-none pointer-events-none"
            aria-hidden="true"
            data-testid="love-equation-mark"
          >
            <span
              className="text-[#2A2724]"
              style={{
                opacity: 0.15,
                fontFamily: "'Cormorant Garamond', 'EB Garamond', Georgia, serif",
                fontStyle: 'italic',
                fontWeight: 400,
                fontSize: '1.15rem',
                letterSpacing: '0.45em',
              }}
            >
              E&nbsp;=&nbsp;mc<sup style={{ fontSize: '0.7em', verticalAlign: 'super' }}>2</sup>
            </span>
          </div>
        </div>
      </main>
    </div>
  );
}
